import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { select, Store } from '@ngrx/store';
import { AgGridAngular  } from '@ag-grid-community/angular';
import { AllModules } from '@ag-grid-enterprise/all-modules';

import { AccountingEntry } from 'app/api/accounting.api.service';
import { AppState } from 'app/reducers';
import {
    agGridLocale,
    EurCurrencyCellRenderer
} from 'app/utils/ag-grid.locale';
import { AmplifyService } from 'aws-amplify-angular';
import * as moment from 'moment';
import { Observable, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    map,
    takeUntil,
    tap
} from 'rxjs/operators';
import {
    selectAccounting,
    selectAccountingLoading
} from './accounting.selectors';
import { CompaniesUISelectors } from '../company/selectors';

@Component({
    selector: 'accounting',
    templateUrl: './accounting.component.html',
    styleUrls: ['./accounting.component.scss'],
    animations: fuseAnimations,
    encapsulation: ViewEncapsulation.None
})
export class AccountingComponent implements OnInit, OnDestroy, AfterViewInit {
    public gridApi;
    public gridColumnApi;

    public columnDefs;
    public defaultColDef;
    public sideBar;
    public statusBar;
    public rowBuffer;
    public localeText;
    public modules;

    keyUp = new Subject<KeyboardEvent>();
    rowData$: Observable<AccountingEntry[]>;
    loading$: Observable<boolean>;

    // Protected
    protected logger: any;

    // Private
    private _unsubscribeAll: Subject<any>;

    @ViewChild('agGrid', { static: false }) agGrid: AgGridAngular;

    constructor(
        protected amplifyService: AmplifyService,
        protected router: Router,
        protected activatedRoute: ActivatedRoute,
        protected store: Store<AppState>
    ) {
        this.logger = this.amplifyService.logger('AccountingComponent');
        this._unsubscribeAll = new Subject();
        this.modules = AllModules;
        this.localeText = agGridLocale.fr;
        this.columnDefs = [
            {
                headerName: 'Date',
                field: 'entryDate',
                valueGetter: params =>
                    params.data
                        ? moment(params.data.entryDate).format('YYYY-MM')
                        : ''
            },
            { headerName: 'Journal', field: 'journal' },
            { headerName: 'Libellé du journal', field: 'journalLib' },
            { headerName: 'N° écriture', field: 'document' },
            { headerName: 'Compte', field: 'account' },
            { headerName: 'Libellé de compte', field: 'accountLabel' },
            { headerName: 'Auxiliaire', field: 'auxiliary' },
            { headerName: 'Libellé de compte auxiliaire', field: 'compAuxLib' },
            { headerName: 'Libellé', field: 'entryLabel' },
            {
                headerName: 'Débit',
                field: 'debit',
                type: 'numericColumn',
                cellRenderer: EurCurrencyCellRenderer
            },
            {
                headerName: 'Crédit',
                field: 'credit',
                type: 'numericColumn',
                cellRenderer: EurCurrencyCellRenderer
            },
            {
                headerName: 'Date de Reference',
                field: 'pieceDate',
                valueGetter: params =>
                    params.data && params.data.pieceDate
                        ? moment(params.data.pieceDate).format('YYYY-MM-DD')
                        : ''
            },
            { headerName: 'Reference', field: 'reference' },
            {
                headerName: 'Date de Lettrage',
                field: 'dateLet',
                valueGetter: params =>
                    params.data && params.data.dateLet
                        ? moment(params.data.dateLet).format('YYYY-MM-DD')
                        : ''
            },
            { headerName: 'Lettrage', field: 'lettering' },
            {
                headerName: 'Date valide',
                field: 'validDate',
                valueGetter: params =>
                    params.data && params.data.validDate
                        ? moment(params.data.validDate).format('YYYY-MM-DD')
                        : ''
            },
            { headerName: 'Montant de devise', field: 'montDevise' },
            // tslint:disable-next-line:indent
			{ headerName: 'Devise', field: 'iDevise' },
            // { headerName: 'Ligne', field: 'row', type: 'numericColumn' }
        ];
        this.defaultColDef = {
            enableValue: true,
            enableRowGroup: true,
            enablePivot: true,
            sortable: true,
            filter: true,
            resizable: true
        };
        this.sideBar = {
            toolPanels: [
                {
                    id: 'columns',
                    labelDefault: 'Colonnes',
                    labelKey: 'columns',
                    iconKey: 'columns',
                    toolPanel: 'agColumnsToolPanel'
                },
                {
                    id: 'filters',
                    labelDefault: 'Filtres',
                    labelKey: 'filters',
                    iconKey: 'filter',
                    toolPanel: 'agFiltersToolPanel'
                }
            ]
        };
        this.statusBar = {
            statusPanels: [
                {
                    statusPanel: 'agTotalRowCountComponent',
                    align: 'left'
                },
                {
                    statusPanel: 'agAggregationComponent'
                },
                { statusPanel: 'agSelectedRowCountComponent' },
                { statusPanel: 'agFilteredRowCountComponent' }
            ]
        };
    }

    ngOnInit(): void {
        this.loading$ = this.store.pipe(select(selectAccountingLoading));
        this.keyUp
            .pipe(
                takeUntil(this._unsubscribeAll),
                map((event: any) => event.target.value),
                debounceTime(250),
                distinctUntilChanged(),
                tap(search => this.gridApi.setQuickFilter(search))
            )
            .subscribe();

        this.rowData$ = this.store.pipe(select(selectAccounting));

        this.store
            .pipe(
                takeUntil(this._unsubscribeAll),
                select(CompaniesUISelectors.selectCurrentCompanyId),
            )
            .subscribe(companyId => {
                window.history.pushState({}, '', `/accounting/${companyId}`);
            });

    }

    ngAfterViewInit(): void {}

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    onGridReady(params): void {
        this.gridApi = params.api;
        this.gridColumnApi = params.columnApi;
    }

    public clearSearch(): void {
        this.gridApi.setQuickFilter('');
    }

    onFirstDataRendered(params): void {
        this.autoSizeAll(params);
    }

    private autoSizeAll(params): void {
        const allColumnIds = params.columnApi
            .getAllColumns()
            .map(column => column.colId);
        setTimeout(() => params.columnApi.autoSizeColumns(allColumnIds), 0);
    }
}
